import { ColumnFilter, SelectOption } from '@globaleris/ui.library'
import { EnumCommodity, NewPosicionPrecio, TradeDetailsResponse } from '../api'
import { FlexibilitiesModel } from '../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { LeadModel } from '../api/Argentia/endpoints/leads/model/leads'
import { Operand } from '../api/Argentia/endpoints/operands/model/operand'
import { PriceCoefficientTable } from '../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import { PriceCalculationTypeCompatibility } from '../api/Argentia/endpoints/pricing/model/priceType'
import { TaskModel } from '../api/Argentia/endpoints/tasks/model/tasks'
import { CommercialModel } from '../api/Argentia/model/commercials/commercial'
import { ContractModel } from '../api/Argentia/model/contracts'
import { DateFilters } from '../model'
import { ClientFileModel } from '../pages/clients/clients.model'
import { ContractProduct, NewContractProduct } from '../pages/contracts/contracts.model'
import { LeadFormSearchFilter } from '../pages/leads/components/searchFilterCard/searchFilterCard'
import { LeadsSearchMode } from '../pages/leads/leads.model'
import { PriceDetails, PriceVersion } from '../pages/pricing/pricing.Model'
import { TaskFormSearchFilter } from '../pages/tasks/components/searchFilterCardTasks/searchFilterCardTasks'
import { EnumEstadoCiclo } from '../api/oData/enums/enumEstadoCiclo'

export interface UserStoreModel {
  id: number
  name: string
  surname: string
  completeName: string
  displayName: string
  domain: string
  favicon: string
  landingPage: string
  theme: string
  roles: RoleStoreModel[]
  menuItems: MenuItemStoreModel[]
  isSuperUser: boolean
  domainClientArea: string
}

export interface RoleStoreModel {
  id: number
  description: string
  resources: ResourceStoreModel[]
}

export interface MenuItemStoreModel {
  id: number
  title: string
  url: string
  urlReturn: string
  icon: string
  order: number
  idParent: number
  idResource: number
}

export interface ResourceStoreModel {
  id: number
  description: number
  edit: boolean
  info: boolean
  readOnly: boolean
  send: boolean
  idCompany?: number
  idBalanceZone?: number
}

export interface UserProfileStoreModel {
  userId: number
  userName: string
  domain: string
  dataBase: string
  clientId: string
  roles: string[]
}

export interface NotificationsStoreModel {
  error: string
  info: string
  isLoading: boolean
}

export interface CompanyStoreModel {
  id: number
  description: string
  default: boolean
  abbreviation: string
  commodities: EnumCommodity[]
}

export interface BalanceZoneStoreModel {
  id: number
  description: string
  code: string
}

export interface ProfileStoreModel {
  logoutHandler?: () => void
  user?: UserStoreModel
  companies?: CompanyStoreModel[]
  activeCompany?: CompanyStoreModel
  balanceZones?: BalanceZoneStoreModel[]
  activeBalanceZone?: BalanceZoneStoreModel
  activeCommodity?: EnumCommodity
}

export interface LayoutStoreModel {
  sideNavAutoCollapse: boolean
  sideHelpIsOpen: boolean
  sideChangelogIsOpen: boolean
  newReleaseNotification: boolean | null
}

export interface AppConfigurationItemModel {
  key: string
  value: string
}

export interface DateFilterModel {
  activeDateFilter?: DateFilters
  initDate?: Date
  endDate?: Date
}

export type CommodityFilterModel = EnumCommodity

export type CustomFilterModel = any

export type DataTableFilter = ColumnFilter[]

export interface PageStateStoreModel {
  page: string
  dataTableFilter?: DataTableFilter
  dataTableSearch?: string
  dataTableRowsPerPage?: number
  dateFilter?: DateFilterModel
  customFilter?: CustomFilterModel
  activeTab?: number
  activeTabPower?: number
  commodityFilter?: EnumCommodity
  showOnlyActivesFilter?: boolean
}

export type PagesStoreModel = PageStateStoreModel[]

export interface RootStoreModel {
  router: any
  notifications: NotificationsStoreModel
  profile: ProfileStoreModel
  layout: LayoutStoreModel
  confirmationDialog: ConfirmationDialogStoreModel
  appConfiguration: AppConfigurationItemModel[]
  pagesState: PagesStoreModel
  pricingState: PricingStoreModel
  tradesState: TradesStoreModel
  tasksState: TasksStoreModel
  leadsState: LeadsStoreModel
  clientsState: ClientsStoreModel
  processing: ProcessingStoreModel
}

export interface ConfirmationDialogConfigurationStoreModel {
  title: string
  isWaiting?: boolean
  message: string
  okBtnLabel: string
  cancelBtnLabel: string
  onClose: (confirm: boolean) => void
  onCancel?: (confirm: boolean) => void
}

export interface InformationDialogConfigurationStoreModel {
  title: string
  isWaiting?: boolean
  message: string
  okBtnLabel: string
  onClose: (confirm: boolean) => void
  informationMode?: boolean
}

export interface ConfirmationDialogStoreModel {
  open: boolean
  configuration?: ConfirmationDialogConfigurationStoreModel &
    InformationDialogConfigurationStoreModel
}

export interface PricingStoreModel {
  price?: PriceDetails
  versions?: PriceVersion[]
  operands?: Operand[]
  calculationTypes?: PriceCalculationTypeCompatibility[]
  coefficients?: PriceCoefficientTable[] | null
}

export interface TradesStoreModel {
  trade?: TradeDetailsResponse
  position?: NewPosicionPrecio
}

export interface ClientsStoreModel {
  clientData?: ClientFileModel
  clientContracts?: ContractModel[]
  clientCommercials?: CommercialModel[]
  activeContract?: ContractModel | null
  activeFlexibility?: FlexibilitiesModel | null
  contractProducts?: ContractProduct[] | null // VALUE, UNDEFINED (unfetched), NULL (onError)
  contractProductsTemp?: NewContractProduct[] | null // VALUE, UNDEFINED (unfetched), NULL (onError)
  isFetchingContract: boolean
  isFetchingContractError: boolean
  isFetchingContractProducts: boolean
  isFetchingContractProductsError: boolean
  isFetchingClientContracts: boolean
  isFetchingClientContractsError: boolean
  isFetchingClientCommercials: boolean
  isFetchingClientCommercialsError: boolean
  isFetchingClientData: boolean
  isFetchingClientDataError: boolean
  isFetchingCountries: boolean
  isFetchingCountriesError: boolean
  isFetchingProvinces: boolean
  isFetchingProvincesError: boolean
  isFetchingMunicipalities: boolean
  isFetchingMunicipalitiesError: boolean
  countries?: SelectOption[]
  searchText?: string
  isActive?: boolean
  showCommunicationFile?: boolean
  showCommunicationAdd?: boolean
  activeTabAditionals?: 'PRICE_COMPONENTS' | 'FLEXIBILITY'
}

export interface TasksStoreModel {
  showTasks: boolean
  showSearchFilter: boolean
  tasksDataTable: TaskModel[]
  tasksSearchFilters: TaskFormSearchFilter
}

export interface LeadsStoreModel {
  searchMode: LeadsSearchMode
  leadsDataTable: LeadModel[]
  leadsSearchFilters: LeadFormSearchFilter
}

// #region ProcessingStoreModel
interface BaseProcessStoreModel {
  // Proceso base con propiedades comunes
  processId: string
  processType: string
  startedAt: Date
}
export interface InvoiceCycleProcessStoreModel extends BaseProcessStoreModel {
  // Proceso de Invoice Cycle
  processType: 'invoiceCycle'
  executionCycleId: number
  currentStatus: EnumEstadoCiclo
  cycleName?: string
  invoiceCycleId?: number
  additionalState?: any
}

export interface GenericProcessStoreModel extends BaseProcessStoreModel {
  // Proceso genérico para otros módulos
  processType: 'generic'
  currentStatus: any
  data: any
}

/**
 * Union type de todos los procesos
 * Agregar nuevos tipos aquí: | MyNewProcessStoreModel
 */
export type ActiveProcessStoreModel = InvoiceCycleProcessStoreModel | GenericProcessStoreModel

export interface ProcessingStoreModel {
  // redux state
  activeProcesses: { [processId: string]: ActiveProcessStoreModel }
}

// #endregion ProcessingStoreModel

export const initialNotificationsStoreState = {
  error: '',
  info: '',
  isLoading: false
}

export const initialConfirmationDialogStoreState = {
  open: false
}

export const initialProfileStoreState = {
  logoutHandler: undefined,
  user: undefined,
  companies: undefined,
  activeCompany: undefined,
  balanceZones: undefined,
  activeBalanceZone: undefined
}

export const initialLayoutStoreState: LayoutStoreModel = {
  sideNavAutoCollapse: true,
  sideHelpIsOpen: false,
  sideChangelogIsOpen: false,
  newReleaseNotification: null
}

export const initialAppConfigurationState = []

export const initialPagesState: PagesStoreModel = []

export const initialPricingStoreState: PricingStoreModel = {}

export const initialTradesStoreState: TradesStoreModel = {}

export const initialClientsStoreState: ClientsStoreModel = {
  isFetchingContract: false,
  isFetchingContractError: false,
  isFetchingContractProducts: false,
  isFetchingContractProductsError: false,
  isFetchingClientContracts: false,
  isFetchingClientContractsError: false,
  isFetchingClientCommercials: false,
  isFetchingClientCommercialsError: false,
  isFetchingClientData: false,
  isFetchingClientDataError: false,
  isFetchingCountries: false,
  isFetchingCountriesError: false,
  isFetchingProvinces: false,
  isFetchingProvincesError: false,
  isFetchingMunicipalities: false,
  isFetchingMunicipalitiesError: false,
  clientData: undefined,
  clientContracts: undefined,
  searchText: undefined,
  isActive: false,
  showCommunicationFile: true,
  showCommunicationAdd: true
}

export const initialTasksStoreState = {
  showTasks: false,
  showSearchFilter: false,
  tasksDataTable: [] as TaskModel[],
  tasksSearchFilters: {} as TaskFormSearchFilter
}

export const initialLeadsStoreState = {
  searchMode: LeadsSearchMode.None,
  leadsDataTable: [] as LeadModel[],
  leadsSearchFilters: {} as LeadFormSearchFilter
}

export const initialRootStoreState = {
  notifications: initialNotificationsStoreState,
  profile: initialProfileStoreState,
  layout: initialLayoutStoreState,
  confirmationDialog: initialConfirmationDialogStoreState,
  appConfiguration: initialAppConfigurationState,
  pagesState: initialPagesState,
  pricingState: initialPricingStoreState,
  clientsState: initialClientsStoreState
}
